import { useEffect, useMemo } from 'react';

import { useReportError } from '@/hooks/useReportError';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { useSystemPresentationTemplateWhere } from '@/modules/presentationTemplates/hooks/useSystemPresentationTemplateWhere';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { getNodes } from '@/utils/graphqlUtils';

import { useOnboardingPresentationTemplatesQuery } from './graphql/OnboardingPresentation.generated';
import { PresentationContent } from './PresentationTemplateSelectionStep.Content';
import { getOrderedAvailableTemplates } from './PresentationTemplateSelectionStep.utils';

export function PresentationBody() {
  const currentUser = useCurrentUser();
  const { id: tenantId } = useTenantDetailsContext();
  const { reportError } = useReportError();
  const systemTemplateWhere = useSystemPresentationTemplateWhere();
  const { data, loading, error } = useOnboardingPresentationTemplatesQuery({
    variables: {
      currentUserId: currentUser?.id ?? '',
      systemTemplateWhere,
    },
    skip: !currentUser?.id,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (error)
      reportError('Failed to load onboarding presentation templates', error);
  }, [error, reportError]);

  const pinnedTemplates = useMemo(
    () => data?.availablePresentationTemplatePins ?? [],
    [data]
  );
  const availableTemplates = useMemo(
    () =>
      getOrderedAvailableTemplates(
        {
          userTemplates: getNodes(data?.userTemplates),
          tenantTemplates: getNodes(data?.tenantTemplates),
          systemTemplates: getNodes(data?.systemTemplates),
        },
        tenantId
      ),
    [data, tenantId]
  );

  return (
    <PresentationContent
      pinnedTemplates={pinnedTemplates}
      availableTemplates={availableTemplates}
      loading={loading}
    />
  );
}
